import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Loader2, Send } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import TurnstileWidget, { turnstileConfigured } from '../components/TurnstileWidget';
import { toast } from 'sonner';
import { useLang } from '../context/LanguageContext';

export default function ForgotPasswordPage() {
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [tsToken, setTsToken] = useState('');
  const captchaRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error(t('fp.errMissing')); return; }
    if (turnstileConfigured && !tsToken) {
      toast.error(t('cf.errCaptcha'));
      return;
    }
    setSending(true);
    try {
      await api.passwordRequestCode(email, tsToken);
      setDone(true);
      // Carry email to the next step (memory-only).
      sessionStorage.setItem('wyse_reset_email', email);
      setTimeout(() => nav('/verify-code'), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('fp.errSend'));
      setTsToken(''); captchaRef.current?.reset();
    } finally { setSending(false); }
  };

  return (
    <>
      <Header />
      <section className="min-h-[70vh] flex items-center justify-center px-6 py-14">
        <div data-testid="forgot-password-card"
             className="w-full max-w-md bg-white rounded-2xl border border-neutral-100 shadow-[0_25px_60px_-30px_rgba(20,17,15,0.25)] p-8 md:p-10">
          <Link to="/login" data-testid="back-to-login"
                className="inline-flex items-center gap-1.5 text-[12.5px] text-neutral-500 hover:text-[#14110f] mb-6">
            <ArrowLeft className="w-3.5 h-3.5" />{t('fp.back')}
          </Link>
          <p className="section-eyebrow mb-2">{t('fp.eyebrow')}</p>
          <h1 className="text-[28px] font-extrabold text-[#14110f] leading-tight">{t('fp.title')}</h1>
          <p className="mt-2 text-[13.5px] text-neutral-500">
            {t('fp.desc')}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-3.5">
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input data-testid="forgot-email" required type="email"
                     value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder={t('fp.emailPh')}
                     className="w-full pl-10 pr-4 py-3.5 rounded-md border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px] text-[#14110f]" />
            </div>

            <TurnstileWidget ref={captchaRef}
                             onVerify={setTsToken}
                             onExpire={() => setTsToken('')}
                             testId="forgot-turnstile" />

            <button type="submit" data-testid="forgot-submit"
                    disabled={sending || done}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-60 text-[#14110f] font-semibold px-7 py-3.5 rounded-md shadow-[0_14px_30px_-10px_rgba(245,179,0,0.7)] transition-all">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {done ? t('fp.checkInbox') : t('fp.send')}
            </button>
          </form>

          <p className="mt-6 text-[11.5px] text-neutral-400 leading-relaxed">
            {t('fp.footer')}
          </p>
        </div>
      </section>
      <Footer />
    </>
  );
}
